/**
 * The workspace's BizForge plan (backend routes/subscription.ts,
 * docs/engineering/BILLING.md).
 *
 *   useSubscriptionStatus  trial / read-only state for everyone (no prices),
 *                          loaded once per session and refreshed after a 402
 *   useUpgradePrompt       the upgrade prompt a 402 from the API opens
 *                          (lib/api.ts interceptor → window event → <UpgradePrompt/>)
 *   loadBillingSummary …   owner-only calls behind Settings → Billing
 */

import { create } from 'zustand'
import { apiClient } from '@/lib/api'

// ─── Types (mirror backend services/platformBilling.ts) ─────────────────────

export type TrialPhase = 'none' | 'trialing' | 'expired' | 'needs_card' | 'active' | 'past_due' | 'suspended' | 'canceled'
export type BillingInterval = 'month' | 'year'

export interface SubscriptionStatus {
  plan: string | null
  planName: string | null
  billingStatus: string | null
  trialEndsAt: string | null
  phase: TrialPhase
  daysLeft: number | null
  readOnly: boolean
  graceDaysLeft: number | null
  limits: { officeSeats: number; crewSeats: number | null; activeAutomations: number | null } | null
  features: Record<string, boolean>
}

export interface PlanDefinition {
  id: string
  name: string
  rank: number
  monthlyPriceCents: number
  annualPriceCents: number
  annualMonthlyPriceCents: number
  tagline: string
  highlighted: boolean
  badge: string | null
  marketingFeatures: string[]
  officeSeats: number
  crewSeats: number | null
  activeAutomations: number | null
  aiCreditsIncludedPerMonth: number
  aiCallsPerMonth?: number | null
}

export interface AddOnDefinition {
  id: string
  name: string
  monthlyPriceCents: number
  annualPriceCents: number
  availableOn: string[]
  includedIn: string[]
  quantity: boolean
}

export interface LaunchOffer {
  name: string
  headline: string
  annualOnly: boolean
  durationMonths: number
  endsOn: string | null
  discountCentsPerYear: Record<string, number>
}

export interface BillingSummary {
  configured: boolean
  requireCardForTrial: boolean
  plan: string | null
  planName: string | null
  billingStatus: string | null
  state: { phase: TrialPhase; daysLeft: number | null; readOnly: boolean; graceDaysLeft: number | null }
  interval: BillingInterval | null
  trialEndsAt: string | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  hasSubscription: boolean
  hasBillingAccount: boolean
  addOns: Record<string, number>
  launchOfferActive: boolean
  seats: { officeUsed: number; officeLimit: number | null; officeIncluded: number | null; extraOfficeSeats: number; crewUsed: number; crewLimit: number | null }
  automations: { active: number; limit: number | null }
  ai: { creditsIncludedPerMonth: number; bundle: string | null; callsPerMonth?: number | null } | null
  nextInvoice: { amountDueCents: number; currency: string; date: string | null } | null
  paymentMethod: { brand: string | null; last4: string | null; expMonth: number | null; expYear: number | null } | null
  catalog: { version: string; currency: string; trialDays: number; plans: PlanDefinition[]; addOns: AddOnDefinition[]; launchOffer: LaunchOffer | null }
}

export interface SubscriptionInvoice {
  id: string
  number: string | null
  status: string | null
  amountDueCents: number
  amountPaidCents: number
  currency: string
  createdAt: string
  periodStart: string | null
  periodEnd: string | null
  hostedInvoiceUrl: string | null
  pdfUrl: string | null
}

export interface PlanSelection {
  plan: string
  interval: BillingInterval
  addOns: Record<string, number>
}

const unwrap = <T,>(res: { data?: { data?: T } & T }): T => (res.data?.data ?? res.data) as T

// ─── Calls ───────────────────────────────────────────────────────────────────

export async function loadSubscriptionStatus(): Promise<SubscriptionStatus> {
  return unwrap(await apiClient.get('/api/subscription/status'))
}

export async function loadBillingSummary(): Promise<BillingSummary> {
  return unwrap(await apiClient.get('/api/subscription'))
}

export async function loadSubscriptionInvoices(): Promise<SubscriptionInvoice[]> {
  return unwrap(await apiClient.get('/api/subscription/invoices'))
}

export async function startCheckout(sel: PlanSelection): Promise<string> {
  return unwrap<{ url: string }>(await apiClient.post('/api/subscription/checkout', sel)).url
}

export async function openBillingPortal(): Promise<string> {
  return unwrap<{ url: string }>(await apiClient.post('/api/subscription/portal', {})).url
}

export async function changeSubscription(change: Partial<PlanSelection>): Promise<BillingSummary> {
  return unwrap(await apiClient.post('/api/subscription/change', change))
}

export async function cancelSubscription(): Promise<BillingSummary> {
  return unwrap(await apiClient.post('/api/subscription/cancel', {}))
}

export async function resumeSubscription(): Promise<BillingSummary> {
  return unwrap(await apiClient.post('/api/subscription/resume', {}))
}

// ─── Formatting ──────────────────────────────────────────────────────────────

export function formatPrice(cents: number, currency = 'usd'): string {
  const whole = cents % 100 === 0
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

export function formatDay(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** What one interval of a plan plus its add-ons costs (before tax and discounts). */
export function selectionTotalCents(sel: PlanSelection, plans: PlanDefinition[], addOns: AddOnDefinition[]): number {
  const plan = plans.find((p) => p.id === sel.plan)
  if (!plan) return 0
  let total = sel.interval === 'year' ? plan.annualPriceCents : plan.monthlyPriceCents
  for (const [id, qty] of Object.entries(sel.addOns)) {
    const a = addOns.find((x) => x.id === id)
    if (!a || qty <= 0 || a.includedIn.includes(sel.plan)) continue
    total += (sel.interval === 'year' ? a.annualPriceCents : a.monthlyPriceCents) * qty
  }
  return total
}

/** The plan's AI allowance in words: AI-answered calls, or AI credits, whichever pricing.json uses. */
export function aiAllowanceText(calls: number | null | undefined, credits: number): string | null {
  if (calls != null) return `${calls.toLocaleString('en-US')} AI-answered calls a month`
  if (credits > 0) return `${credits.toLocaleString('en-US')} AI credits a month`
  return null
}

export const PHASE_LABEL: Record<TrialPhase, string> = {
  none: 'No plan limits',
  trialing: 'Free trial',
  expired: 'Trial ended',
  needs_card: 'Card needed',
  active: 'Active',
  past_due: 'Payment failed',
  suspended: 'Read-only',
  canceled: 'Plan ended',
}

// ─── Status store ────────────────────────────────────────────────────────────

interface StatusState {
  status: SubscriptionStatus | null
  loading: boolean
  refresh: () => Promise<void>
  clear: () => void
}

export const useSubscriptionStatus = create<StatusState>((set, get) => ({
  status: null,
  loading: false,
  async refresh() {
    if (get().loading) return
    set({ loading: true })
    try {
      set({ status: await loadSubscriptionStatus() })
    } catch {
      /* the banner simply doesn't show */
    } finally {
      set({ loading: false })
    }
  },
  clear() {
    set({ status: null })
  },
}))

// ─── Upgrade prompt (opened by a 402 from the API) ───────────────────────────

export type UpgradeReason = 'read_only' | 'plan_feature' | 'seat_limit' | 'automation_limit'

export interface UpgradePromptState {
  open: boolean
  code: UpgradeReason | null
  message: string
  requiredPlan: string | null
  show: (p: { code: UpgradeReason; message: string; requiredPlan?: string | null }) => void
  close: () => void
}

export const useUpgradePrompt = create<UpgradePromptState>((set) => ({
  open: false,
  code: null,
  message: '',
  requiredPlan: null,
  show: ({ code, message, requiredPlan }) => set({ open: true, code, message, requiredPlan: requiredPlan ?? null }),
  close: () => set({ open: false }),
}))

const UPGRADE_CODES = new Set<string>(['read_only', 'plan_feature', 'seat_limit', 'automation_limit'])

/** Called by the API client for every 402: opens the prompt and refreshes the banner. */
export function handlePaymentRequired(body: unknown): void {
  const b = (body ?? {}) as { code?: string; error?: string; requiredPlan?: string | null }
  if (!b.code || !UPGRADE_CODES.has(b.code)) return
  useUpgradePrompt.getState().show({ code: b.code as UpgradeReason, message: b.error ?? '', requiredPlan: b.requiredPlan })
  if (b.code === 'read_only') void useSubscriptionStatus.getState().refresh()
}

/** Same name as lib/api.ts PAYMENT_REQUIRED_EVENT (kept literal so test mocks of the API client don't need it). */
export const PAYMENT_REQUIRED_EVENT = 'bizforge:payment-required'

if (typeof window !== 'undefined') {
  window.addEventListener(PAYMENT_REQUIRED_EVENT, (e) => handlePaymentRequired((e as CustomEvent).detail))
}
